/* eslint-disable react/prop-types */
import classNames from 'classnames';
import Image from 'components/Image';
import InPostCta from 'components/InPostCta';
import InPostResourceCta from 'components/InPostResourceCta';
import parse, { domToReact, attributesToProps } from 'html-react-parser';
import slugify from 'slugify';

const componentClasses = ['blog-cta', 'resource-cta'];

const slugifyOpts = {
  lower: true,
  strict: true,
};

const findText = (children) => {
  if (Array.isArray(children) && children.length >= 1) {
    for (let index = 0; index < children.length; index += 1) {
      const child = children[index];
      if (child.data) {
        return child.data;
      }
      return findText(child.children);
    }
  } else {
    return '';
  }
  return null;
};

const findComponent = (children) => {
  if (Array.isArray(children) && children.length >= 1) {
    for (let index = 0; index < children.length; index += 1) {
      const child = children[index];
      if (
        child.type === 'tag' &&
        child.name === 'span' &&
        componentClasses.includes(child.attribs?.class)
      ) {
        // console.log(child.type, child.name, child.attribs, child.children);
        return child;
      }
      if (child.children) return findComponent(child.children);
    }
  } else {
    return null;
  }
  return null;
};

const contentsOptions = {
  replace: ({ type, name, attribs, children }) => {
    if (type === 'tag' && name === 'h2') {
      const heading = findText(children);
      const href = attribs.id || slugify(heading, slugifyOpts);

      return (
        <li>
          <a href={`#${href}`}>{heading}</a>
        </li>
      );
    }

    return <></>;
  },
};

const contentOptions = {
  // eslint-disable-next-line consistent-return
  replace: ({ type, name, attribs, children }) => {
    // Wrap iFrames
    if (type === 'tag' && name === 'iframe') {
      const props = attributesToProps(attribs);
      return (
        <span className="block">
          <iframe
            title={attribs.title}
            src={attribs.src}
            frameBorder={0}
            allowFullScreen={attribs.allowfullscreen}
            className="aspect-video w-full"
          >
            {domToReact(children, contentOptions)}
          </iframe>
        </span>
      );
    }

    // Fix Google Doc styling
    if (
      type === 'tag' &&
      name === 'span' &&
      attribs.style === 'font-weight: 400;'
    ) {
      return <>{domToReact(children, contentOptions)}</>;
    }

    // Remove any remaining style tags
    delete attribs?.style;

    // Link Headings
    if (type === 'tag' && name === 'h2' && !attribs.id) {
      attribs.id = slugify(findText(children), slugifyOpts);
    }

    // Process Links
    if (type === 'tag' && name === 'a') {
      delete attribs.rel;
      delete attribs.target;

      // Fix external links
      if (
        attribs.href &&
        !attribs.href.startsWith('https://www.yesware.com') &&
        !attribs.href.startsWith('#')
      ) {
        attribs.rel = 'noopener';
        attribs.target = '_blank';
      }

      // Remove host from blog relative links
      // TODO: Replace with next/image once /blog/ bypass list is resolved
      if (
        attribs.href &&
        attribs.href.startsWith('https://www.yesware.com/blog/')
      ) {
        attribs.href = attribs.href.replace('https://www.yesware.com', '');
      }
    }

    // Process Images
    if (type === 'tag' && name === 'img') {
      if (attribs.src.startsWith('//')) {
        attribs.src = `https:${attribs.src}`;
      }

      if (attribs.width && attribs.height) {
        return (
          <span
            className={classNames([
              attribs.class &&
                attribs.class.includes('alignright') &&
                'float-right',
              attribs.class &&
                attribs.class.includes('alignleft') &&
                'float-left',
              attribs.class &&
                attribs.class.includes('aligncenter') &&
                'flex justify-around',
            ])}
          >
            <Image
              alt={attribs.alt || ''}
              src={attribs.src}
              width={attribs.width}
              height={attribs.height}
            />
          </span>
        );
      }

      return (
        <span className="relative aspect-[4/3] w-full">
          <Image
            alt={attribs.alt || ''}
            src={attribs.src}
            layout="fill"
            objectFit="contain"
          />
        </span>
      );
    }

    // Blog CTA
    if (type === 'tag' && name === 'span' && attribs?.class === 'blog-cta') {
      return (
        <InPostCta
          href={attribs['data-href']}
          icon={attribs['data-icon']}
          heading={attribs['data-heading']}
          text={attribs['data-text']}
          button={attribs['data-button']}
        />
      );
    }

    // Resource CTA
    if (
      type === 'tag' &&
      name === 'span' &&
      attribs?.class === 'resource-cta'
    ) {
      return (
        <InPostResourceCta
          name={attribs['data-name']}
          image={attribs['data-image']}
          heading={attribs['data-heading']}
          text={attribs['data-text']}
          button={attribs['data-button']}
        />
      );
    }

    // Nested CTAs
    if (type === 'tag' && name === 'p') {
      const component = findComponent(children);

      if (component) {
        // Blog CTA
        if (component.attribs?.class === 'blog-cta') {
          return (
            <InPostCta
              href={component.attribs['data-href']}
              icon={component.attribs['data-icon']}
              heading={component.attribs['data-heading']}
              text={component.attribs['data-text']}
              button={component.attribs['data-button']}
            />
          );
        }

        // Resource CTA
        if (component.attribs?.class === 'resource-cta') {
          return (
            <InPostResourceCta
              name={component.attribs['data-name']}
              image={component.attribs['data-image']}
              heading={component.attribs['data-heading']}
              text={component.attribs['data-text']}
              button={component.attribs['data-button']}
            />
          );
        }
      }
    }
  },
};

// eslint-disable-next-line import/prefer-default-export
export const contentParser = (rawContent) => ({
  contents: parse(rawContent, contentsOptions),
  content: parse(rawContent, contentOptions),
});
